import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, ShieldOff, Copy, Check, AlertTriangle, HelpCircle, Lightbulb } from 'lucide-react';

export default function AdminSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Enrollment flow state
  const [enrolling, setEnrolling] = useState(false);
  const [secret, setSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [saving, setSaving] = useState(false);

  // Disable flow state
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const loadStatus = async () => {
    try {
      const res = await api.get('/auth/mfa/status');
      setStatus(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadStatus(); }, []);

  const startEnroll = async () => {
    setSaving(true);
    try {
      const res = await api.post('/auth/mfa/setup');
      setSecret(res.data.secret);
      setOtpauthUri(res.data.otpauth_uri);
      const dataUrl = await QRCode.toDataURL(res.data.otpauth_uri, { width: 260, margin: 2 });
      setQrDataUrl(dataUrl);
      setEnrolling(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo iniciar la configuración de MFA'); }
    setSaving(false);
  };

  const confirmEnroll = async () => {
    if (!verifyCode.trim()) { toast.error('Ingresa el código de tu app de autenticación'); return; }
    setSaving(true);
    try {
      const res = await api.post('/auth/mfa/verify-setup', { code: verifyCode.trim() });
      setBackupCodes(res.data.backup_codes);
      toast.success('MFA activado');
      loadStatus();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Código inválido'); }
    setSaving(false);
  };

  const finishEnrollment = () => {
    setEnrolling(false); setSecret(''); setOtpauthUri(''); setQrDataUrl('');
    setVerifyCode(''); setBackupCodes(null);
  };

  const handleDisable = async () => {
    if (!disablePassword.trim() || !disableCode.trim()) { toast.error('Completa contraseña y código'); return; }
    setSaving(true);
    try {
      await api.post('/auth/mfa/disable', { password: disablePassword, code: disableCode.trim() });
      toast.success('MFA desactivado');
      setShowDisableForm(false); setDisablePassword(''); setDisableCode('');
      loadStatus();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo desactivar'); }
    setSaving(false);
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-settings">
      <div>
        <h1 className="text-xl font-bold text-[#0F172A]">Configuración de la cuenta</h1>
        <p className="text-sm text-[#64748B] mt-1">Seguridad de tu cuenta de plataforma.</p>
      </div>

      <div className="bg-white border rounded-xl p-6 space-y-4" data-testid="mfa-card">
        <div className="flex items-center gap-3">
          {status?.enabled ? <ShieldCheck className="w-6 h-6 text-emerald-600" /> : <ShieldOff className="w-6 h-6 text-amber-500" />}
          <div>
            <h3 className="text-sm font-bold text-[#0F172A]">Verificación en dos pasos (MFA)</h3>
            <p className="text-xs text-[#64748B]">
              {status?.enabled ? 'Activo — cada inicio de sesión pide un código adicional.' : 'No está activo. Se recomienda para cuentas de plataforma.'}
            </p>
          </div>
        </div>

        {/* Already enabled — show disable flow */}
        {status?.enabled && !showDisableForm && (
          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowDisableForm(true)} data-testid="mfa-open-disable">
            Desactivar MFA
          </Button>
        )}
        {status?.enabled && showDisableForm && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3" data-testid="mfa-disable-form">
            <p className="text-xs text-red-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Confirma tu contraseña y un código actual para desactivar MFA.</p>
            <div>
              <Label className="text-xs">Contraseña</Label>
              <Input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)} className="h-9 text-sm" data-testid="mfa-disable-password" />
            </div>
            <div>
              <Label className="text-xs">Código (app o de respaldo)</Label>
              <Input value={disableCode} onChange={e => setDisableCode(e.target.value)} className="h-9 text-sm" data-testid="mfa-disable-code" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setShowDisableForm(false)}>Cancelar</Button>
              <Button size="sm" variant="destructive" onClick={handleDisable} disabled={saving} data-testid="mfa-disable-confirm">
                {saving ? 'Desactivando...' : 'Desactivar'}
              </Button>
            </div>
          </div>
        )}

        {/* Not enabled, not enrolling yet */}
        {!status?.enabled && !enrolling && (
          <Button size="sm" onClick={startEnroll} disabled={saving} data-testid="mfa-start-enroll">
            {saving ? 'Preparando...' : 'Activar MFA'}
          </Button>
        )}

        {/* Enrolling — QR + verify step */}
        {!status?.enabled && enrolling && !backupCodes && (
          <div className="space-y-4" data-testid="mfa-enroll-form">
            <p className="text-xs text-[#64748B]">
              Escanea este código con Google Authenticator, Authy o una app similar. Si no puedes escanear, ingresa la clave manualmente.
            </p>
            {qrDataUrl && <img src={qrDataUrl} alt="Código QR de MFA" className="border rounded-lg" data-testid="mfa-qr-image" />}
            <div className="flex items-center gap-2">
              <code className="text-xs bg-[#F8FAFC] border rounded px-2 py-1 flex-1 truncate" data-testid="mfa-secret-text">{secret}</code>
              <Button size="sm" variant="outline" onClick={copySecret} data-testid="mfa-copy-secret">
                {copiedSecret ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <div>
              <Label className="text-xs">Código de 6 dígitos</Label>
              <Input value={verifyCode} onChange={e => setVerifyCode(e.target.value)} placeholder="123456"
                className="h-9 text-sm text-center tracking-[0.3em]" data-testid="mfa-verify-code-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={finishEnrollment}>Cancelar</Button>
              <Button size="sm" onClick={confirmEnroll} disabled={saving} data-testid="mfa-verify-confirm">
                {saving ? 'Verificando...' : 'Verificar y activar'}
              </Button>
            </div>
          </div>
        )}

        {/* Backup codes — shown once */}
        {backupCodes && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3" data-testid="mfa-backup-codes">
            <p className="text-xs text-amber-800 flex items-center gap-1 font-medium">
              <AlertTriangle className="w-3.5 h-3.5" /> Guarda estos códigos de respaldo — no se mostrarán de nuevo. Cada uno funciona una sola vez si pierdes acceso a tu app de autenticación.
            </p>
            <div className="grid grid-cols-2 gap-2 font-mono text-xs">
              {backupCodes.map(c => <div key={c} className="bg-white border rounded px-2 py-1 text-center">{c}</div>)}
            </div>
            <Button size="sm" onClick={finishEnrollment} data-testid="mfa-backup-codes-done">Ya los guardé</Button>
          </div>
        )}
      </div>

      <div className="bg-white border rounded-xl p-6 space-y-4" data-testid="help-tooltips-section">
        <div className="flex items-center gap-3">
          <HelpCircle className="w-6 h-6 text-[#0F172A]" />
          <div>
            <h3 className="text-sm font-bold text-[#0F172A]">Ayuda y tooltips</h3>
            <p className="text-xs text-[#64748B]">Cómo se usan las ayudas del Super Admin (íconos “?” y textos de contexto).</p>
          </div>
        </div>
        <ul className="space-y-3 text-sm text-[#475569]">
          <li className="flex gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <span><strong className="text-[#0F172A]">Tooltips “?”</strong> — Al pasar el mouse o tocar el ícono de ayuda junto a un campo (Plan Builder, Infraestructura, etc.) aparece la explicación sin salir de la pantalla.</span>
          </li>
          <li className="flex gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <span><strong className="text-[#0F172A]">Owner (modo Dios)</strong> — Como Owner puedes crear, editar e impersonar sin bloqueos de mutación. Los Administradores solo ven los módulos que les asignes en Usuarios → Super Users.</span>
          </li>
          <li className="flex gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <span><strong className="text-[#0F172A]">Infraestructura por marca</strong> — Cada bloque de color (Twilio rojo, Gupshup morado, Google amarillo/verde, Meta azul, Instagram rosa, TikTok negro, LinkedIn blanco) agrupa solo la UX; no cambia cómo se guardan las credenciales.</span>
          </li>
          <li className="flex gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <span><strong className="text-[#0F172A]">Consumo & Costos</strong> — Todo el panel muestra montos en USD. Si un evento se guardó en MXN se convierte con 1 USD = 20 MXN (ajustable en el simulador de Plan Builder).</span>
          </li>
          <li className="flex gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <span><strong className="text-[#0F172A]">Simulador de planes</strong> — En Plan Builder al editar un plan, el simulador estima el costo de infraestructura al 100% de los límites vs el precio de venta.</span>
          </li>
        </ul>
        <p className="text-[11px] text-[#94A3B8]">
          White Label y personalización de textos de ayuda por tenant se documentarán en un módulo aparte.
        </p>
      </div>
    </div>
  );
}
